import React, { useState, useEffect } from "react";
import { useWhiteboard, usePubSub } from "@videosdk.live/react-sdk";
import {
  PlayIcon,
  StopIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
} from "@heroicons/react/24/outline";
import { useMeetingAppContext } from "../../MeetingAppContextDef";
import { sideBarModes } from "../../utils/common";

const WhiteboardPanel = ({ panelHeight }) => {
  const { startWhiteboard, stopWhiteboard, whiteboardUrl } = useWhiteboard();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const { setSideBarMode } = useMeetingAppContext();

  // PubSub for whiteboard notifications
  const { publish: publishWhiteboardStatus } = usePubSub("WHITEBOARD_STATUS");

  // Listen for whiteboard status changes from other participants
  usePubSub("WHITEBOARD_STATUS", {
    onMessageReceived: (data) => {
      const { message } = data;
      try {
        const statusData = JSON.parse(message);
        if (statusData.action === "WHITEBOARD_STARTED") {
          // Auto-open whiteboard sidebar when someone starts it
          setSideBarMode(sideBarModes.WHITEBOARD);
        }
      } catch (error) {
        console.error("Error parsing whiteboard status:", error);
      }
    },
  });

  // Monitor whiteboard URL changes and notify other participants
  useEffect(() => {
    if (whiteboardUrl) {
      // Notify all participants that whiteboard has started
      publishWhiteboardStatus(
        JSON.stringify({
          action: "WHITEBOARD_STARTED",
          timestamp: Date.now(),
        }),
        { persist: false }
      );
    }
  }, [whiteboardUrl, publishWhiteboardStatus]);

  const handleStartWhiteboard = () => {
    startWhiteboard();
  };

  const handleStopWhiteboard = () => {
    stopWhiteboard();
    // Notify participants that whiteboard stopped
    publishWhiteboardStatus(
      JSON.stringify({
        action: "WHITEBOARD_STOPPED",
        timestamp: Date.now(),
      }),
      { persist: false }
    );
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  return (
    <div 
      className="flex flex-col h-full bg-gray-800"
      style={{ height: panelHeight }}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-gray-600 bg-gray-750">
        <h3 className="text-white font-medium">Whiteboard</h3>
        <div className="flex items-center space-x-2">
          {whiteboardUrl && (
            <button
              onClick={toggleFullscreen}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-600 rounded-md transition-colors"
              title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
            >
              {isFullscreen ? (
                <ArrowsPointingInIcon className="w-4 h-4" />
              ) : (
                <ArrowsPointingOutIcon className="w-4 h-4" />
              )}
            </button>
          )}
          
          {!whiteboardUrl ? (
            <button
              onClick={handleStartWhiteboard}
              className="flex items-center space-x-2 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm font-medium transition-colors"
            >
              <PlayIcon className="w-4 h-4" />
              <span>Start Whiteboard</span>
            </button>
          ) : (
            <button
              onClick={handleStopWhiteboard}
              className="flex items-center space-x-2 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm font-medium transition-colors"
            >
              <StopIcon className="w-4 h-4" />
              <span>Stop Whiteboard</span>
            </button>
          )}
        </div>
      </div>

      {/* Whiteboard Content */}
      <div className="flex-1 relative">
        {whiteboardUrl ? (
          <div className="absolute inset-0">
            <iframe
              src={whiteboardUrl}
              width="100%"
              height="100%"
              frameBorder="0"
              allow="camera; microphone; display-capture"
              className="w-full h-full bg-white"
              title="VideoSDK Whiteboard"
            />
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-center p-6">
            <div className="w-24 h-24 bg-gray-700 rounded-lg flex items-center justify-center mb-4">
              <svg
                className="w-12 h-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <h4 className="text-white text-lg font-medium mb-2">
              Start Collaborative Whiteboard
            </h4>
            <p className="text-gray-400 text-sm mb-4 max-w-xs">
              Share ideas visually with all participants in real-time. Draw, write, and brainstorm together.
            </p>
            <button
              onClick={handleStartWhiteboard}
              className="flex items-center space-x-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors"
            >
              <PlayIcon className="w-5 h-5" />
              <span>Start Whiteboard Session</span>
            </button>
            <p className="text-gray-500 text-xs mt-3">
              All participants will be able to collaborate on the same whiteboard
            </p>
          </div>
        )}
      </div>

      {/* Status Bar */}
      {whiteboardUrl && (
        <div className="p-2 border-t border-gray-600 bg-gray-750">
          <div className="flex items-center justify-between text-xs text-gray-400">
            <span className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span>Whiteboard Active</span>
            </span>
            <span>Collaborative Session</span>
          </div>
        </div>
      )}

      {/* Fullscreen Modal */}
      {isFullscreen && whiteboardUrl && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-90 flex flex-col">
          {/* Fullscreen Header */}
          <div className="flex items-center justify-between p-4 bg-gray-900 border-b border-gray-700">
            <h3 className="text-white font-medium">Whiteboard - Fullscreen</h3>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleStopWhiteboard}
                className="flex items-center space-x-2 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm font-medium transition-colors"
              >
                <StopIcon className="w-4 h-4" />
                <span>Stop</span>
              </button>
              <button
                onClick={toggleFullscreen}
                className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-md transition-colors"
                title="Exit Fullscreen"
              >
                <ArrowsPointingInIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          {/* Fullscreen Whiteboard */}
          <div className="flex-1">
            <iframe
              src={whiteboardUrl}
              width="100%"
              height="100%"
              frameBorder="0"
              allow="camera; microphone; display-capture"
              className="w-full h-full"
              title="VideoSDK Whiteboard - Fullscreen"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default WhiteboardPanel;
